import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";
import { WhatsappIcon } from "./WhatsappIcon";

type ActionButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  tone?: "edit" | "delete" | "neutral";
  children: ReactNode;
};

export function AdminActionButton({ label, tone = "neutral", className = "", children, ...props }: ActionButtonProps) {
  return <button type="button" className={`admin-icon-button ${tone} ${className}`.trim()} title={label} aria-label={label} {...props}>{children}</button>;
}

export function EditIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16v4Z" /><path d="m13.5 6.5 4 4" /></svg>;
}

export function DeleteIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" /></svg>;
}

export function LockIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="10" width="14" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>;
}

export function ProtectedBadge({ label = "Protegido" }: { label?: string }) {
  return <span className="protected-lock-badge" title={label} aria-label={label}><LockIcon /><span>{label}</span></span>;
}

export type ActionIconName =
  | "previous"
  | "next"
  | "reply"
  | "replyAll"
  | "forward"
  | "delete"
  | "logout"
  | "add"
  | "archive"
  | "calendar"
  | "cancel"
  | "check"
  | "close"
  | "copy"
  | "download"
  | "edit"
  | "external"
  | "filter"
  | "grid"
  | "import"
  | "mail"
  | "message"
  | "play"
  | "refresh"
  | "report"
  | "save"
  | "search"
  | "send"
  | "settings"
  | "stop"
  | "upload"
  | "view"
  | "users";

const iconPaths: Record<ActionIconName, ReactNode> = {
  previous: <path d="m14 5-7 7 7 7" />,
  next: <path d="m10 5 7 7-7 7" />,
  reply: <path d="m10 5-7 6 7 6M3 11h10a8 8 0 0 1 8 8" />,
  replyAll: <><path d="m8 5-6 6 6 6m6-12-6 6 6 6M8 11h5a8 8 0 0 1 8 8" /></>,
  forward: <path d="m14 5 7 6-7 6M21 11h-10a8 8 0 0 0-8 8" />,
  delete: <><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" /></>,
  logout: <><path d="M10 4H4v16h6M9 12h12m-5-5 5 5-5 5" /></>,
  add: <path d="M12 5v14M5 12h14" />,
  archive: <><path d="M4 7h16v13H4zM3 4h18v3H3z" /><path d="M9 11h6" /></>,
  calendar: <><rect x="3.5" y="5" width="17" height="15" rx="2" /><path d="M8 3v4m8-4v4M3.5 10h17" /></>,
  cancel: <path d="m6 6 12 12M18 6 6 18" />,
  check: <path d="m5 12 4 4L19 6" />,
  close: <path d="m6 6 12 12M18 6 6 18" />,
  copy: <><rect x="8" y="8" width="11" height="12" rx="2" /><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h2" /></>,
  download: <><path d="M12 3v12m-5-5 5 5 5-5" /><path d="M5 20h14" /></>,
  edit: <><path d="M4 20h4L19 9l-4-4L4 16v4Z" /><path d="m13.5 6.5 4 4" /></>,
  external: <><path d="M14 4h6v6M20 4l-9 9" /><path d="M18 13v6H5V6h6" /></>,
  filter: <path d="M4 6h16l-6 7v5l-4 2v-7L4 6Z" />,
  grid: <><rect x="4" y="4" width="6" height="6" rx="1" /><rect x="14" y="4" width="6" height="6" rx="1" /><rect x="4" y="14" width="6" height="6" rx="1" /><rect x="14" y="14" width="6" height="6" rx="1" /></>,
  import: <><path d="M12 3v12m-5-5 5 5 5-5" /><path d="M5 20h14" /></>,
  mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" /></>,
  message: <path d="M4 5h16v12H9l-5 4V5Z" />,
  play: <path d="m8 5 11 7-11 7V5Z" />,
  refresh: <><path d="M20 7v5h-5" /><path d="M19 12a7 7 0 1 1-2-5" /></>,
  report: <><path d="M5 3h10l4 4v14H5V3Z" /><path d="M15 3v5h5M8 17v-4m4 4V9m4 8v-6" /></>,
  save: <><path d="M5 3h12l3 3v15H4V3h1Z" /><path d="M8 3v6h8V3M8 21v-7h8v7" /></>,
  search: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m15.5 15.5 5 5" /></>,
  send: <path d="m3 11 18-8-7 18-3-7-8-3Zm8 3 4-4" />,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M19 13.5v-3l-2-.7-.5-1.2.9-1.9-2.1-2.1-1.9.9-1.2-.5L11.5 3h-3l-.7 2-1.2.5-1.9-.9-2.1 2.1.9 1.9L3 9.8l-2 .7v3l2 .7.5 1.2-.9 1.9 2.1 2.1 1.9-.9 1.2.5.7 2h3l.7-2 1.2-.5 1.9.9 2.1-2.1-.9-1.9.5-1.2 2-.7Z" transform="scale(.82) translate(2.6 2.6)" /></>,
  stop: <rect x="6" y="6" width="12" height="12" rx="1" />,
  upload: <><path d="M12 17V5m-5 5 5-5 5 5" /><path d="M5 20h14" /></>,
  view: <><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" /><circle cx="12" cy="12" r="2.5" /></>,
  users: <><circle cx="9" cy="8" r="3" /><path d="M3 20v-2a6 6 0 0 1 12 0v2M16 5a3 3 0 0 1 0 6m2 3a5 5 0 0 1 3 4v2" /></>,
};

export function ActionIcon({ name }: { name: ActionIconName }) {
  return <svg className="action-icon" viewBox="0 0 24 24" aria-hidden="true">{iconPaths[name]}</svg>;
}

type IconActionButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "title"> & {
  label: string;
  icon: ActionIconName;
  tone?: "primary" | "secondary" | "danger" | "success";
  busy?: boolean;
};

export function IconActionButton({ label, icon, tone = "secondary", busy = false, className = "", ...props }: IconActionButtonProps) {
  const effectiveLabel = busy ? `${label}…` : label;
  return <button type={props.type || "button"} className={`action-icon-button ${tone} ${busy ? "busy" : ""} ${className}`.trim()} title={effectiveLabel} aria-label={effectiveLabel} {...props}><ActionIcon name={busy ? "refresh" : icon} /><span className="visually-hidden">{effectiveLabel}</span></button>;
}

type WhatsappActionButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "title"> & {
  label: string;
  tone?: "primary" | "secondary" | "danger" | "success";
};

export function WhatsappActionButton({ label, tone = "success", className = "", ...props }: WhatsappActionButtonProps) {
  return <button type={props.type || "button"} className={`action-icon-button ${tone} whatsapp-action-button ${className}`.trim()} title={label} aria-label={label} {...props}><WhatsappIcon /><span className="visually-hidden">{label}</span></button>;
}

type IconActionLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "children" | "title"> & {
  label: string;
  icon: ActionIconName;
  tone?: "primary" | "secondary" | "danger" | "success";
};

export function IconActionLink({ label, icon, tone = "secondary", className = "", ...props }: IconActionLinkProps) {
  return <a className={`action-icon-button ${tone} ${className}`.trim()} title={label} aria-label={label} {...props}><ActionIcon name={icon} /><span className="visually-hidden">{label}</span></a>;
}
